<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="true"
                :showAddNew="true"
                :showExtraBtn="true"
                btnTitle="Add Product"
                extraBtnTitle="New Group"
                title="Product List"
                @onClickAddNewButton="openProductAddModal"
                @onClickExtraBtn="openGroupAddModal"
                @onClickCloseButton="onClickCloseButton"
            />
        </div>

        <div class="row p-2">
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label" for="select2-basic">Product Type</label>
                <v-select
                    v-model="product_type"
                    :options="productTypeArray"
                    label="value"
                    :reduce="value => value.key"
                    placeholder="Select Product Type"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label" for="select2-basic">Product Name</label>
                <AsyncSelect
                    placeholder="Select Product"
                    v-model="product_id"
                    label="name"
                    :api-service="fetchEndpointProductList"
                    :format-label="option => option.name"
                    :additional-query="{ product_type: product_type ? product_type : '' }"
                    ref="productField"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label" for="select2-basic">Short Description</label>
                <input
                    v-model.trim="short_description"
                    class="form-control"
                >
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label" for="select2-basic">Full Description</label>
                <input
                    v-model.trim="full_description"
                    class="form-control"
                >
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-4">
                <label class="form-label" for="select2-basic">Supplier</label>
                <AsyncSelect
                    placeholder="Supplier Name, ID, Mobile No"
                    v-model="contactProfile"
                    :api-service="fetchContactProfiles"
                    :additional-query="{'type' : 'supplier'}"
                    :format-label="formatPatientLabel"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label" for="modalEditUserStatus">Select Status</label>
                <v-select
                    placeholder="Select Status"
                    v-model="selectedStatus"
                    :options="productStatusList"
                    label="name"
                    :reduce="item => item.value"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3 mt-auto">
                <button @click="getProducts" class="btn btn-primary waves-effect waves-float waves-light">
                    Go
                </button>
                <button @click="exportReport" class="btn btn-block btn-success ms-1 waves-effect waves-float waves-light"
                >
                    Export
                </button>
            </div>
            <div class="col-12 col-sm-6 col-md-4 col-lg-3 mt-auto">
                
            </div>
        </div>

        <div class="col-12 px-2 my-2">
            <ListTable
                :tableItems="tableItems"
                :productStatusList="productStatusList"
                :inventoryMethods="inventoryMethods"
                :inventorySlugType="inventorySlugType"
            />
        </div>

        <div class="px-2 position-absolute bottom-0">
            <Pagination
              :offset="10"
              @onPageChange="onPageChange"
              ref="pagination"
            />
        </div>

        <ProductAddModal
            :accountHeads="accountHeads"
            :selectedSpecifications="selectedSpecifications"
            @update:selectedSpecifications="selectedSpecifications = $event"
            :specifications="specifications"
            :clue="clue"
            :units="units"
            :group="productGroup"
            :formData="product"
            :inventoryMethods="inventoryMethods"
            :inventorySlugType="inventorySlugType"
            :setBrandValue="setBrandValue"
            :removeBrandValue="removeBrandValue"
            @onClickSubmitProduct="handleProductSubmit"
            @onCloseModal="onCloseModal"
            ref="productModal"
        />
        <GroupAddModal
            modalTitle="Add Group"
            ref="groupModal"
            :type="homeContent.product_type"
            :productSlug="homeContent.product_slug"
            :formData="groupAddData"
            :status="homeContent.status"
            :group="productGroup"
            :depIntervalMethods="depIntervalMethods"
            :depIntervalIntervals="depIntervalIntervals"
            @onSubmitGroup="handleGroupSubmit"
        />

        <Loader v-if="modalLoading"/>
    </div>
</template>

<script setup>
    import { ref, onMounted, computed, inject } from 'vue';
    import TitleButton from '@/components/atom/TitleButton';
    import handleInventory from "@/services/modules/inventory";
    import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
    import {useRoute, useRouter} from 'vue-router'
    import Pagination from "@/components/atom/Pagination.vue";
    import ListTable from '@/components/molecule/company/hospital/service-resources/ProductListTable.vue';
    import Loader from '@/components/atom/LoaderComponent';
    import ProductAddModal from '@/components/molecule/company/inventory/product-service/ProductAddModal';
    import GroupAddModal from '@/components/molecule/company/inventory/product-service/GroupAddModal';
    import {useStore} from "vuex";
    import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
    import handleContact from '@/services/modules/contact'

    const {
        fetchHome,
        fetchEndpointProductList,
        fetchEndpointProductDetails,
        fetchClue,
        fetchAccountHeads,
        fetchAccountHeadsBySlags,
        fetchUnit,
        fetchGroup,
        fetchSpecification,
        fetchDepIntervalMethods,
        storeProduct,
        storeGroup
    } = handleInventory();
    const showError = inject('showError');
    const showSuccess = inject('showSuccess');
    const $route = useRoute();
    const $router = useRouter();
    const $store = useStore();

    const productTypeArray = ref([]);
    const product_type = ref(null);
    const product_id = ref(null);
    const short_description = ref('');
    const full_description = ref('');
    const contactProfile = ref(null);
    const tableItems = ref([]);
    const page = computed(() => $route.query.page);
    const offset = ref(10);
    const productStatusList = ref([]);
    const inventoryMethods = ref([]);
    const inventorySlugType = ref([]);
    const selectedStatus = ref('active');
    const { fetchContactProfiles } = handleContact();
    const { formatPatientLabel } = useAsyncDropdownHelper();

    const productModal = ref(null);
    const groupModal = ref(null);
    const modalLoading = ref(false);
    const homeContent = ref({});
    const clue = ref([]);
    const units = ref([]);
    const productGroup = ref([]);
    const accountHeads = ref([]);
    const specifications = ref({});
    const selectedSpecifications = ref([]);
    const depIntervalMethods = ref([]);
    const depIntervalIntervals = ref([]);
    const product = ref({
        company_id: '',
        product_head_id: null,
        contact_profile_id: null,
        name: '',
        specifications: {},
        unit_id: null,
        purchase_price: '',
        purchase_account_head_id: null,
        sales_price: '',
        sales_account_head_id: null,
        cost_of_sales: '',
        cost_of_sales_account_head_id: null,
        inventory_account_head_id: null,
        default_purchase_unit_id: null,
        default_sales_unit_id: null,
        default_shipping_unit_id: null,
        alternative_unit_ids: [],
        sku: '',
        barcode_sku: '',
        short_description: '',
        full_description: '',
        image: null,
        status: 'active',
        additional_images: [],
        vat_rate: '',
        inventory_method: 'fifo',
        slug_type: '',
        slug: ''
    });
    const groupAddData = ref({
        name: '',
        product_type: null,
        status: 'active',
        company_id: '',
        head_group_id: null,
        depreciation_method: null,
        depreciation_interval: null,
        depreciation_rate: null,
        tax_depreciation_rate: null,
        product_slug: null
    });

    onMounted(() => {
        Promise.all([
            fetchHome().then((res) => {
                if(res.status) {
                    homeContent.value = res.data
                    let productType = res.data.product_type
                    inventoryMethods.value = [];
                    inventorySlugType.value = [];
                    const allowedStatuses = ['active', 'inactive'];
                    productTypeArray.value = Object.keys(productType).map(key => ({
                                                key,
                                                value: productType[key],
                                            }));

                    productStatusList.value = Object.keys(res.data.status)
                            .filter(key => allowedStatuses.includes(key))
                            .map(key => ({
                                value: key,
                                name: res.data.status[key]
                    }))

                    Object.keys(res.data.inventory_methods).forEach((val)=>{
                        inventoryMethods.value.push({
                            id : val,
                            name : res.data.inventory_methods[val],
                        })
                    });
                    Object.keys(res.data.inventory_slug_type).forEach((val)=>{
                      inventorySlugType.value.push({
                        id : val,
                        name : res.data.inventory_slug_type[val],
                      })
                    });
                }
            })
        ]).catch(e => showError('Something went wrong'))
        loadModalData();
    })

    const loadModalData = () => {
        const companyId = $route.params.companyId;
        const slags = ['purchase_accounts', 'sales_accounts', 'cost_of_sales'];
        const slagQuery = `?company_id=${companyId}` + slags.map(s => `&slags[]=${s}`).join('');
        const depQuery = `?company_id=${companyId}`;
        fetchUnit(companyId).then((res) => { if(res.status) units.value = res.data });
        fetchAccountHeads(companyId).then((res) => {
            if(! res.status) return;
            accountHeads.value = res.data;
            setDefaultHead(res.data);
        });
        fetchAccountHeadsBySlags(slagQuery).then((res) => { if(res.status) setDefaultSlagHead(res.data) });
        fetchClue(companyId).then((res) => { if(res.status) clue.value = res.data });
        fetchSpecification(companyId).then((res) => { if(res.status) specifications.value = res.data });
        fetchDepIntervalMethods(depQuery).then((res) => {
            if(! res.status) return;
            depIntervalMethods.value = res.data.methods;
            depIntervalIntervals.value = res.data.intervals;
        });
        loadProductGroup();
    }

    const loadProductGroup = () => {
        const productType = product_type.value || 'finished_goods';
        fetchGroup($route.params.companyId, productType).then((res) => {
            if(res.status) productGroup.value = res.data;
        });
    }

    const setDefaultHead = (data) => {
        if(! data.length) return;
        const firstInventoryAccount = data.find(i => i.account_slug === 'inventory_assets');
        if(firstInventoryAccount) product.value.inventory_account_head_id = firstInventoryAccount.id;
    }

    const setDefaultSlagHead = (data) => {
        if(! data) return;
        if(data.purchase_accounts.length) product.value.purchase_account_head_id = data.purchase_accounts[0].id;
        if(data.sales_accounts.length) product.value.sales_account_head_id = data.sales_accounts[0].id;
        if(data.cost_of_sales.length) product.value.cost_of_sales_account_head_id = data.cost_of_sales[0].id;
    }

    const setBrandValue = (name, id) => {
        product.value.specifications[name] = id;
    }

    const removeBrandValue = (name) => {
        delete product.value.specifications[name];
    }

    const resetProductForm = () => {
        product.value.name = '';
        product.value.product_head_id = null;
        product.value.unit_id = null;
        product.value.purchase_price = '';
        product.value.sales_price = '';
        product.value.cost_of_sales = '';
        product.value.sku = '';
        product.value.barcode_sku = '';
        product.value.short_description = '';
        product.value.full_description = '';
        product.value.vat_rate = '';
        product.value.alternative_unit_ids = [];
        product.value.inventory_method = 'fifo';
        product.value.slug_type = '';
        product.value.slug = '';
        product.value.specifications = {};
    }

    const onCloseModal = () => {
        selectedSpecifications.value = [];
        product.value.specifications = {};
        resetProductForm();
    }

    const openProductAddModal = () => {
        loadProductGroup();
        productModal.value.toggleModal();
    }

    const openGroupAddModal = () => {
        loadProductGroup();
        groupAddData.value.product_type = product_type.value || 'finished_goods';
        groupModal.value.toggleModal();
    }

    const appendProductFormData = () => {
        const fd = new FormData();
        Object.keys(product.value).forEach((key) => {
            const isSkipped = ['additional_images', 'specifications', 'image', 'alternative_unit_ids'].includes(key);
            if(! isSkipped && product.value[key] !== null) fd.append(key, product.value[key]);
        });
        if(product.value.image) fd.append('image', product.value.image);
        product.value.additional_images.forEach((value, i) => {
            fd.append(`additional_images[${i}]`, value);
        });
        const alternativeUnitIds = product.value.alternative_unit_ids.map((unitId) => {
            return { unit_id: unitId };
        });
        fd.append('specifications', JSON.stringify(product.value.specifications));
        fd.append('alternative_unit_ids', JSON.stringify(alternativeUnitIds));
        return fd;
    }

    const handleProductSubmit = async () => {
        product.value.company_id = $route.params.companyId;
        if(product.value.inventory_method === null) delete product.value.inventory_method;
        try {
            productModal.value.loading = true;
            const fd = appendProductFormData();
            const res = await storeProduct(fd);
            if(! res.status) {
                showError(res.message);
                return;
            }
            showSuccess(res.message);
            productModal.value.toggleModal();
            onCloseModal();
            getProducts();
        } catch (err) {
            handleModalError(err);
        } finally {
            productModal.value.loading = false;
        }
    }

    const handleGroupSubmit = async () => {
        if(groupAddData.value.product_type !== 'fixed_assets') {
            groupAddData.value.depreciation_method = null;
            groupAddData.value.depreciation_interval = null;
            groupAddData.value.depreciation_rate = 0;
            groupAddData.value.tax_depreciation_rate = 0;
        }
        try {
            groupModal.value.loading = true;
            groupAddData.value.company_id = $route.params.companyId;
            const res = await storeGroup(groupAddData.value);
            if(! res.status) {
                showError(res.message);
                return;
            }
            groupModal.value.toggleModal();
            resetGroupForm();
            loadProductGroup();
            showSuccess(res.message);
            getProducts();
        } catch (err) {
            handleModalError(err);
        } finally {
            groupModal.value.loading = false;
        }
    }

    const resetGroupForm = () => {
        groupAddData.value = {
            name: '',
            product_type: null,
            status: 'active',
            company_id: '',
            head_group_id: null,
            depreciation_method: null,
            depreciation_interval: null,
            depreciation_rate: null,
            tax_depreciation_rate: null,
            product_slug: null
        };
    }

    const handleModalError = (err) => {
        if(! err.response) {
            showError('Something is wrong. Check your connectivity!!');
            return;
        }
        showError(err.response.message);
    }

    const getQuery = () => {
        let query = '';
        query += `?company_id=${$route.params.companyId}`;
        query += '&offset=' + offset.value
        if (page.value) query += '&page=' + page.value
        if(product_id.value) query += `&product_id=${product_id.value.id}`;
        if(product_type.value) query += `&product_type=${product_type.value}`;
        if(short_description.value) query += `&short_description=${short_description.value}`;
        if(full_description.value) query += `&full_description=${full_description.value}`;
        if(contactProfile.value) query += `&contact_profile_id=${contactProfile.value.id}`;
        if(selectedStatus.value) query += `&status=${selectedStatus.value}`;
        
        return query;
    }

    const getProducts = async(resetPage = true) => {
        if(resetPage) {
            let routeQuery = Object.assign({}, $route.query)
            routeQuery.page = 1
            await $router.push({path: $route.path, query: routeQuery})
        }

        let query = getQuery();

        fetchEndpointProductDetails(query).then((res) => {
            tableItems.value = res.data.data;
            setPagination(res.data)
        })
    }

    const setPaginationData = (data) => {
        $store.commit('setPaginationData', data)
    }

    const setPagination = (data) => {
        setPaginationData({
          records: data.total,
          perPage: data.per_page,
          currentPage: data.current_page,
        })
    }

    const onPageChange = async (page) => {
        let routeQuery = Object.assign({}, $route.query)
        routeQuery.page = page
        await $router.push({path: $route.path, query: routeQuery})
        getProducts(false);
    }

    const onClickCloseButton = () => {
        if ($route.query.redirect_route) {
            $router.push({ name: $route.query.redirect_route, params: $route.params, query: $route.query });
            return;
        }
        const params = Object.assign({}, $route.params, { accountType: $route.params.accountType || 'finished_goods' });
        $router.push({ name: 'inventory-list', params: params, query: $route.query });
    }

    const exportReport = () => {
        let token = localStorage.getItem('token');

        let url = `${process.env.VUE_APP_BASE_URL}/export/v2/inventory/products/list/endpoint/details${getQuery()}&_token=${token}&is_export=1`;
        let a = document.createElement('a');
        a.setAttribute('href', url);
        a.click();
    }

</script>